/** @jsx jsx */
import React, { Fragment, type PropsWithChildren, type ReactNode } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { usePreloadedQuery, graphql, type PreloadedQuery } from 'react-relay';
import { HydrationStatsReporter } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import { TopNavEntry } from '@jira/navigation-apps__page-container-entries-top-nav-entry/src/TopNavEntry.tsx';
import { HorizontalNavEntry } from '@jira/navigation-apps__page-container-entries-horizontal-nav-entry/src/HorizontalNavEntry.tsx';
import { Tokens } from '@jira/platform__project-theme-tokens/src/constants.tsx';
import { isStableUIEnabledForTests } from '@jira/platform__is-stable-ui-enabled-for-tests/src/isStableUIEnabledForTests.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useJsmGracePeriodBanner } from '@jira/servicedesk__jsm-grace-period-banner/src/useJsmGracePeriodBanner.tsx';
import { JSMGracePeriodBannerAsync } from '@jira/servicedesk__jsm-grace-period-banner/src/JSMGracePeriodBannerAsync.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useIsChromeless } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/chromeless/index.tsx';
import { useIsFullscreen } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { LayoutRemoteStateUpdater } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/layout-remote-state-updater/index.tsx';
import { RenderOnClient } from '@jira/ssr__ssr-toolkit/src/render-on-client.tsx';
import { Main } from '@jira/design-system__navigation-system/src/PageLayout.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { useProjectTheme } from '@jira/platform__project-theme-page-container/src/controllers/project-theme/index.tsx';
import {
	getSharedLayout,
	sharedLayout,
} from '@jira/platform__layouts-jira-shared-layout/src/sharedLayout.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { isSystemNavPath } from '@jira/navigation-apps__sidebar-common/src/utils/get-navigation-path/system/index.tsx';
import { isIssuesNavPath } from '@jira/navigation-apps__sidebar-common/src/utils/get-navigation-path/issues/index.tsx';
import { JiraTopBannerPlacement } from '@jira/post-office__top-banner/src/index.tsx';
import { IntegrationsWhichRequireNavigationRoot } from '@jira/platform__layouts-jira-shared-layout/src/ui/integrations/index.tsx';
import { PreviewPanel } from '@jira/issue__preview-panels/src/PreviewPanel.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { BannerContent } from '../banner/BannerContent.tsx';
import { GlobalComponents } from '../global-components/index.tsx';
import { RightSidebar } from '../right-sidebar/index.tsx';
import { RedirectEntrypointContainer } from '../redirect/index.tsx';
import { RightAdsPanel } from '../right-ads-panel/RightAdsPanel.tsx';
import type { JiraSpaPageContainerQuery } from './__generated__/JiraSpaPageContainerQuery.graphql';
import { JiraPageLayout } from './JiraPageLayout.tsx';
import { GlobalThemeSetter } from './global-theme-setter/GlobalThemeSetter.tsx';
import { ProjectThemeSetter } from './project-theme-setter/ProjectThemeSetter.tsx';
import { PageContainerSuccessTracker } from './success-tracker/PageContainerSuccessTracker.tsx';
import { JiraSpaGlobalBottomRightCorner } from './JiraSpaGlobalBottomRightCorner.tsx';
import { JiraPageContainerFallback } from './JiraPageContainerFallback.tsx';

const SidebarEntry = importCond<
	typeof import('@jira/nav-platform-integration__sidebar/src/NavPlatformSidebar.tsx'),
	typeof import('@jira/navigation-apps__page-container-entries-sidebar-entry/src/SidebarEntry.tsx')
>(
	'navx-global-side-nav',
	'@jira/nav-platform-integration__sidebar/src/NavPlatformSidebar.tsx',
	'@jira/navigation-apps__page-container-entries-sidebar-entry/src/SidebarEntry.tsx',
);

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="page-container-v2-main-wrapper">{children}</UFOSegment>
	),
	Fragment,
);

const mainWrapperStyles = cssMap({
	root: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		' > span[data-ep-placeholder-id="route_entry_point"]': {
			// EntryPointPlaceholder span element will stop the height from being 100%.
			// this makes content height can be set to 100% even if the span is present.
			// https://jplat.jira.atlassian.cloud/browse/BLU-3362
			display: 'contents',
		},
	},
	themed: {
		// When an image background is applied via the background css var the image
		// will be darkened in dark mode and lightened in light mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundBlendMode: Tokens.ELEVATION_SURFACE_BLEND_MODE,
	},
});

/**
 * These test IDs are used in pollinator tests. Do not change lightly.
 */
const ROOT_TEST_ID = 'page-layout.root';
const MAIN_TEST_ID = 'page-layout.main';

/**
 * The default page chrome for a Jira application.
 * We currently use an islands architecture, where each section of the layout
 * is relatively independent of one-another, and render a skeleton for each piece
 * of the page layout when it is loading.
 *
 * To achieve this, each section must suspend independent of each-other.
 * Be sure that each component rendered here has its own suspense boundary.
 *
 * @note
 * If there is no suspense boundary above this component and the preloaded query call suspends,
 * the user won't see the intended layout skeleton. They will most likely see a blank page, or
 * whatever generic placeholder has been configured in that higher boundary.
 */
const DefaultLayout = ({
	children,
	queryReference,
}: {
	children: ReactNode;
	queryReference: PreloadedQuery<JiraSpaPageContainerQuery>;
}) => {
	const [isChromeless] = useIsChromeless();
	const [isFullscreen] = useIsFullscreen();

	const showBanner =
		!isChromeless &&
		!isFullscreen &&
		((!isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion')) ||
			!fg('jpx-870-stable-ui-for-criterion'));
	const showTopNav = !isChromeless && !isFullscreen;
	const showSidebar = !isChromeless && !isFullscreen;
	const showHorizontalNav = !isChromeless;
	const showRightSidebar =
		!isChromeless &&
		!isFullscreen &&
		((!isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion')) ||
			!fg('jpx-870-stable-ui-for-criterion'));
	const showLayoutRemoteStateUpdater = !isChromeless && !isFullscreen;

	const data = usePreloadedQuery(
		graphql`
			query JiraSpaPageContainerQuery($cloudId: ID!) @preloadable {
				...BannerContent
			}
		`,
		queryReference,
	);

	return (
		<JiraPageLayout testId={ROOT_TEST_ID}>
			<RenderOnClient>
				<IntegrationsWhichRequireNavigationRoot />
			</RenderOnClient>
			{showBanner && (
				<JSErrorBoundary id="banner" packageName="jiraPageContainerV2" fallback="unmount">
					<Placeholder name="banner_skeleton" fallback={null}>
						<BannerContent bannerFragment={data} />
					</Placeholder>
				</JSErrorBoundary>
			)}
			{showTopNav && (
				<UFOSegment name="nav4.topNav">
					<JSErrorBoundary id="top-nav" packageName="jiraPageContainerV2" fallback="unmount">
						<TopNavEntry />
					</JSErrorBoundary>
				</UFOSegment>
			)}
			{showSidebar && (
				<JSErrorBoundary id="sidebar" packageName="jiraPageContainerV2" fallback="flag">
					<SidebarEntry />
				</JSErrorBoundary>
			)}
			<MainWrapper>
				{showHorizontalNav && <HorizontalNavEntry />}
				{children}
			</MainWrapper>
			{showRightSidebar && (
				<JSErrorBoundary id="right-sidebar" packageName="jiraPageContainerV2" fallback="unmount">
					<RightSidebar />
				</JSErrorBoundary>
			)}
			{fg('jira-glance-panel') && (
				<JSErrorBoundary fallback="unmount" id="preview-panel" packageName="jiraPageContainerV2">
					<PreviewPanel />
				</JSErrorBoundary>
			)}
			{fg('ads-panel-system-jira-admin') && (
				<JSErrorBoundary id="dst-panel" packageName="jiraPageContainerV2" fallback="unmount">
					<RightAdsPanel />
				</JSErrorBoundary>
			)}
			{showLayoutRemoteStateUpdater && (
				<JSErrorBoundary
					packageName="jiraPageContainerV2"
					id="layout-remote-state-updater"
					fallback="unmount"
				>
					<LayoutRemoteStateUpdater />
				</JSErrorBoundary>
			)}
		</JiraPageLayout>
	);
};

function MainWrapper(props: { children: ReactNode }) {
	const { hasTheming } = useProjectTheme();
	const { isEnabled: isJsmGracePeriodBannerEnabled } = useJsmGracePeriodBanner();

	const currentRoute = useRouterRoute();
	const isIssueOrSystemAdminPage =
		isSystemNavPath(currentRoute.path) || isIssuesNavPath(currentRoute.path);

	return (
		<Main testId={MAIN_TEST_ID}>
			<MaybeUFOSegment>
				<div
					data-vc="page-container-v2-main-wrapper"
					// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
					style={
						hasTheming
							? {
									// must be an inline style due to firefox bug with blob URLs: see https://atlassian.slack.com/archives/C02785CLSTW/p1770156142296829
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
									background: Tokens.ELEVATION_SURFACE,
								}
							: undefined
					}
					css={[mainWrapperStyles.root, hasTheming && mainWrapperStyles.themed]}
				>
					{isJsmGracePeriodBannerEnabled && <JSMGracePeriodBannerAsync />}
					{isIssueOrSystemAdminPage && <JiraTopBannerPlacement />}
					{props.children}
				</div>
			</MaybeUFOSegment>
		</Main>
	);
}

const useRouterRoute = createRouterSelector((state) => state.route);

const Layout = (props: {
	children: ReactNode;
	queryReference: PreloadedQuery<JiraSpaPageContainerQuery>;
}) => {
	const { LayoutOverrideComponent } = useRouterRoute();
	if (LayoutOverrideComponent !== undefined) {
		return <LayoutOverrideComponent>{props.children}</LayoutOverrideComponent>;
	}
	return <DefaultLayout {...props} />;
};

const Passthrough = ({ children }: PropsWithChildren<{ name?: string; fallback?: unknown }>) => (
	<>{children}</>
);

const WholePagePlaceholder = componentWithFG(
	'jpx-463-spa-deprioritise-whole-page-container',
	Placeholder,
	Passthrough,
);

const JustLayoutPlaceholder = componentWithFG(
	'jpx-463-spa-deprioritise-just-layout',
	Placeholder,
	Passthrough,
);

export const JiraSpaPageContainer = (props: {
	children: ReactNode;
	queryReference: PreloadedQuery<JiraSpaPageContainerQuery> | null | undefined;
}): JSX.Element => {
	const layout = fg('jpx-870-stable-ui-for-criterion') ? getSharedLayout() : sharedLayout;

	// Here, we wrap the entire page structure in a suspense boundary. This might appear overly defensive,
	// but it's to change how React progressive hydration chooses its component render order.
	//
	// If we were to wrap only the component that is most likely to suspend - the Layout component -
	// that would cause React to lower the render priority of that part of the page... meaning everything else renders before it.
	// If SSR achieves VC100, that's not a problem... but since many of our app's routes still have client-bound rendering,
	// it lengthens the time until discovering and starting those things, meaning we lengthen the time until VC90 / VC100.
	//
	// We need to do what we can to prioritise rendering Layout, but the best we can do in React to make it important
	// is to make everything around it less important. At the same time, we need to ensure the user doesn't see a broken page
	// if something does suspend. Wrapping the whole contents is the best compromise; everything inside the boundary has
	// an equal (lowest) priority.
	return (
		<WholePagePlaceholder
			name="spa_page_container"
			fallback={<JiraPageContainerFallback reason="spa_page_container_fallback" />}
		>
			<RedirectEntrypointContainer />
			<GlobalThemeSetter />
			<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
				<ProjectThemeSetter />
				<JustLayoutPlaceholder
					name="spa_layout"
					fallback={<JiraPageContainerFallback reason="spa_layout_fallback" />}
				>
					{props?.queryReference ? (
						<Layout queryReference={props.queryReference}>{props.children}</Layout>
					) : (
						<JiraPageContainerFallback reason="spa_no_query_reference" />
					)}
				</JustLayoutPlaceholder>
				{/* Deprioritise shared global components when progressive hydration is happening */}
				<Placeholder name="global_components">
					<GlobalComponents sharedGlobalComponents={layout.globalComponents} />
				</Placeholder>
				{isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion') ? null : (
					<Placeholder name="bottom_right_corner">
						<JiraSpaGlobalBottomRightCorner />
					</Placeholder>
				)}
				<Placeholder name="telemetry">
					<PageContainerSuccessTracker />
					<HydrationStatsReporter />
				</Placeholder>
			</ContextualAnalyticsData>
		</WholePagePlaceholder>
	);
};
